import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as LucideIcons from 'lucide-react';

const RELEVANT_ICONS = [
    'ShieldCheck', 'TrendingUp', 'TriangleAlert', 'Database',
    'Activity', 'BarChart3', 'PieChart', 'FileText',
    'Users', 'Target', 'Layers', 'Lock'
];

const GRADIENT_THEMES = [
    { class: 'from-red-500 to-red-700', label: 'High Risk (Red)' },
    { class: 'from-green-500 to-green-700', label: 'Low Risk (Green)' },
    { class: 'from-yellow-500 to-yellow-700', label: 'Medium Risk (Yellow)' },
    { class: 'from-blue-500 to-blue-700', label: 'Total (Blue)' },
    { class: 'from-purple-500 to-purple-700', label: 'Purple' },
    { class: 'from-indigo-500 to-indigo-700', label: 'Indigo' },
    { class: 'from-pink-500 to-pink-700', label: 'Pink' },
    { class: 'from-orange-500 to-orange-700', label: 'Orange' },
];

const RiskCardModal = ({ isOpen, onClose, onSubmit, card, loading, loanType }) => {
    const [formData, setFormData] = useState({
        title: '', // Title like "High Risk"
        description: '', // Value like "2%"
        icon_name: 'ShieldCheck',
        gradient_from: 'from-red-500 to-red-700',
        display_order: 0,
        is_active: true,
        loan_type: loanType
    });

    useEffect(() => {
        if (card) {
            setFormData({
                ...card,
                is_active: card.is_active === 1 || card.is_active === true
            });
        } else {
            setFormData({
                title: '',
                description: '',
                icon_name: 'ShieldCheck',
                gradient_from: 'from-red-500 to-red-700',
                display_order: 0,
                is_active: true,
                loan_type: loanType
            });
        }
    }, [card, loanType]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100">
                    <h2 className="text-2xl font-bold text-slate-800">
                        {card ? 'Edit Risk Card' : 'Add New Risk Card'}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                        <XMarkIcon className="h-6 w-6 text-slate-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Card Title
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                placeholder="e.g., High Risk"
                            />
                        </div>

                        {/* Value/Description */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Value / Description
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                placeholder="e.g., 2%"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Display Order */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Display Order
                            </label>
                            <input
                                type="number"
                                value={formData.display_order}
                                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                            />
                        </div>

                        {/* Active Status */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Status
                            </label>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                className={`w-full flex items-center justify-between px-6 py-3 rounded-2xl border transition-all ${formData.is_active
                                    ? 'bg-green-50 border-green-200 text-green-700'
                                    : 'bg-slate-50 border-slate-200 text-slate-500'
                                    }`}
                            >
                                <span className="font-semibold">{formData.is_active ? 'Active' : 'Inactive'}</span>
                                <div className={`w-10 h-6 rounded-full relative transition-all ${formData.is_active ? 'bg-green-500' : 'bg-slate-300'}`}>
                                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.is_active ? 'left-5' : 'left-1'}`}></div>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Icon Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-4">
                            Select Icon
                        </label>
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                            {RELEVANT_ICONS.map((iconName) => {
                                const IconComp = LucideIcons[iconName];
                                return (
                                    <button
                                        key={iconName}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, icon_name: iconName })}
                                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${formData.icon_name === iconName
                                            ? 'border-primary-500 bg-primary-50 text-primary-600'
                                            : 'border-slate-100 bg-white text-slate-400 hover:border-slate-200'
                                            }`}
                                    >
                                        <IconComp className="h-6 w-6" />
                                        <span className="text-[10px] font-medium">{iconName}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Gradient Theme Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-4">
                            Select Color Theme
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {GRADIENT_THEMES.map((theme) => (
                                <button
                                    key={theme.label}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, gradient_from: theme.class })}
                                    className={`p-3 rounded-xl border-2 transition-all text-left ${formData.gradient_from === theme.class
                                        ? 'border-primary-500 bg-white ring-2 ring-primary-100'
                                        : 'border-slate-100 bg-white hover:border-slate-200'
                                        }`}
                                >
                                    <div className={`h-8 w-full rounded-lg bg-gradient-to-r ${theme.class} mb-2`}></div>
                                    <span className="text-xs font-semibold text-slate-600">{theme.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </form>

                <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl font-semibold text-slate-600 hover:bg-slate-200 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-lg"
                    >
                        {loading ? 'Saving...' : card ? 'Update Card' : 'Create Card'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RiskCardModal;
